import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserData } from "hooks/UserContext";
import { getCookie } from "lib/cookies";
import { getCustomerFromMemory } from "lib/sharedSessionMemory";

// SHA-256 hash function for verifying data integrity
async function sha256Hex(input) {
  const data = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

const CHECKOUT_SALT = "z{*'";

export default function Hotels() {
  const [iframeUrl, setIframeUrl] = useState(null);
  const [error, setError] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(125);
  const { user } = useUserData();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  useLayoutEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyHeight = document.body.style.height;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.height = "100%";

    const header = document.querySelector("header.header-wl");
    if (!header) return;

    const update = () => {
      const next = Math.ceil(header.getBoundingClientRect().height || 0);
      if (next > 0) setHeaderHeight(next);
    };

    update();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(header);
    }

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      if (ro) ro.disconnect();
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.height = originalBodyHeight;
    };
  }, []);

  useEffect(() => {
    try {
      if (!user || !user.balance) return;

      const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
      const envisionUrl = process.env.REACT_APP_ENVISION_URL;
      const hasPendingBanner = !!sessionStorage.getItem(
        "pending_banner_redirect",
      );
      const fullUrl = `${envisionUrl}/?channel=${channel}${hasPendingBanner ? "&bannerRedirect=true" : ""}`;

      setIframeUrl(fullUrl);
    } catch (err) {
      console.error("Erro ao montar iframe:", err);
      setError("Erro interno ao carregar o conteúdo.");
    }
  }, [user]);

  useEffect(() => {
    const sendAuthData = (source) => {
      const wlToken = getCookie("wlt");
      const customer = getCustomerFromMemory();
      const userData = customer?.data || null;
      const balance = user?.balance?.data ?? 0;

      if (!wlToken || !source) return;

      const payload = { token: wlToken, userData, balance };
      console.log("[Hotels] >>> Enviando AUTH_DATA", {
        userData: !!userData,
        balance,
      });
      source.postMessage({ type: "AUTH_DATA", payload }, "*");
    };

    const handleMessage = async (event) => {
      const data = event.data;

      if (data?.type === "HANDSHAKE_START") {
        console.log("[Hotels] <<< Recebido HANDSHAKE_START");
        sendAuthData(event.source);
      }

      if (data?.type === "HANDSHAKE_COMPLETE") {
        console.log(
          "[Hotels] <<< Recebido HANDSHAKE_COMPLETE - Handshake finalizado",
        );

        const pendingBanner = sessionStorage.getItem("pending_banner_redirect");
        if (pendingBanner) {
          try {
            const bannerData = JSON.parse(pendingBanner);
            if (bannerData.bannerType === "hotel") {
              sessionStorage.removeItem("pending_banner_redirect");
              event.source.postMessage(
                {
                  type: "REDIRECT_RESULT",
                  payload: {
                    result_type: "search_hotel",
                    bannerData: bannerData.data,
                  },
                },
                "*",
              );
              console.log(
                "[Hotels] >>> Enviando REDIRECT_RESULT (hotel)",
                bannerData.data,
              );
            }
          } catch (err) {
            console.error("[Hotels] Erro ao processar banner redirect:", err);
            sessionStorage.removeItem("pending_banner_redirect");
          }
        }
      }

      if (data && data.type === "ga4_event") {
        const ga4Payload = data.payload;
        if (window.dataLayer) {
          window.dataLayer.push(ga4Payload);
        }
      }

      // Check if this is a checkout message
      if (data?.message === "start-checkout" && data?.payload) {
        try {
          const payload = data.payload;
          const receivedHash = data.hash;

          // Verify data integrity by comparing hash
          const computedHash = await sha256Hex(payload + CHECKOUT_SALT);

          if (receivedHash !== computedHash) {
            console.error("Hash mismatch - data integrity check failed");
            setError("Erro de validação dos dados do checkout.");
            return;
          }

          // Save data to localStorage encoded in base64 for the checkout page
          // Use TextEncoder to handle UTF-8 characters (like accents in Portuguese)
          const encoder = new TextEncoder();
          const uint8Array = encoder.encode(payload);
          const encodedData = btoa(String.fromCharCode(...uint8Array));
          localStorage.setItem("envision", encodedData);

          // Redirect to checkout page
          navigate("/loja/orderresumepag/?checkout=hotel");
        } catch (err) {
          console.error("Error processing checkout message:", err);
          setError("Erro ao processar dados do checkout.");
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [navigate, user]);

  const handleIframeLoad = () => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    if (iframeRef.current) {
      iframeRef.current.blur();
    }

    scrollToTop();
  };

  if (error) {
    return (
      <div style={{ textAlign: "center", marginTop: "100px", color: "red" }}>
        {error}
      </div>
    );
  }

  if (!iframeUrl) {
    return (
      <p style={{ textAlign: "center", marginTop: "100px" }}>Carregando...</p>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: 1,
      }}
    >
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        onLoad={handleIframeLoad}
        title="Safra Hoteis"
        tabIndex="-1"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          overflow: "hidden",
          paddingTop: `${headerHeight}px`,
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}
